import { useEffect, useMemo, useState } from "react";
import { Avatar } from "@/utils/ui/Avatar/Avatar";
import { FilterOption, InputComponentProps } from "./Filters";
import { Display_Value_Key as dvk } from "../Create/utils";
import { useDebounce } from "../../utils";

interface AutoSelectExtraProps {
  onInputChangeCallback?: (params: { name: string; keyword: string }) => void;
  updatedOptions?: Record<string, FilterOption[]>;
  loader?: boolean;
}

type Props = InputComponentProps & AutoSelectExtraProps;

const RenderAutoSelect: React.FC<Props> = ({
  filter,
  options = [],
  onChange,
  preSelected,
  onApplyStateChange,
  onInputChangeCallback,
  updatedOptions,
  loader = false,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);

  const debouncedInput = useDebounce(input, 250);

  const effectiveOptions = useMemo(() => {
    const dynamic = updatedOptions?.[filter.name] ?? options;
    if (input === "") return dynamic;
    return dynamic.filter((o) =>
      o.label.toLowerCase().includes(input.toLowerCase())
    );
  }, [updatedOptions, options, filter.name, input]);

  const toggleSelection = (value: string, label: string) => {
    setSelectedValues((prev) => {
      if (prev.includes(value)) {
        // Remove the value
        return prev?.filter((v) => v !== value);
      } else {
        // Add the value
        return [...prev, value];
      }
    });

    setSelectedLabels((prev) => {
      if (prev.includes(label)) {
        // Remove the label
        return prev?.filter((l) => l !== label);
      } else {
        // Add the label
        return [...prev, label];
      }
    });
  };

  const handleApply = () => {
    if (selectedValues.length > 0) {
      onChange(filter.name, selectedValues, selectedLabels);
    }
  };

  // useEffect(() => {
  //   if (preSelected[filter.name]) {
  //     const values = preSelected[filter.name] as string[];
  //     const labels = preSelected[`${filter.name}displayValue`] as string[] | string;
  //     setSelectedValues(values);
  //     setSelectedLabels(Array.isArray(labels) ? labels : labels ? [labels] : []);
  //   }
  // }, []);
  useEffect(() => {
    if (preSelected[filter.name]) {
      setSelectedValues(preSelected[filter.name]);
      setSelectedLabels(preSelected[filter.name + dvk]);
    }
  }, []);

  // useEffect(() => {
  //   if (preSelected[filter.name]) {
  //     setSelectedValues(preSelected[filter.name]);
  //     setSelectedLabels(preSelected[filter.name + dvk]);
  //   }
  // }, []);

  useEffect(() => {
    onApplyStateChange?.(selectedValues.length === 0, handleApply, "Apply");
  }, [selectedValues]);

  // Call the debounced callback when debouncedInput changes
  useEffect(() => {
    if (debouncedInput && onInputChangeCallback) {
      onInputChangeCallback({ name: filter.name, keyword: debouncedInput });
    }
  }, [debouncedInput]);

  // Control loader visibility
  useEffect(() => {
    if (loader === false) return;
    if (input) {
      const hasMatchingOption = effectiveOptions?.some((option) =>
        option?.label?.toLowerCase().includes(input.toLowerCase())
      );
      if (!hasMatchingOption && onInputChangeCallback) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
        setIsLoadingComplete(true);
      }
    }
  }, [input, effectiveOptions, loader]);

  return (
    <div className="px-3">
      <div className="mb-3 py-3 border-bottom">
        <div className="position-relative">
          <input
            className="form-control form-control-sm"
            type="text"
            value={input}
            onChange={(e) => {
              const keyword = e.target.value;
              setInput(keyword);
              setIsLoadingComplete(false);
              if (loader && keyword) {
                setIsLoading(true);
              } else {
                setIsLoading(false);
              }
            }}
            placeholder={`search ${filter.displayName.toLowerCase()}`}
          />
          {loader && isLoading && input && !isLoadingComplete && (
            <div
              className="position-absolute bottom-0 start-0 w-100"
              style={{ height: "2px", left: 0, bottom: "-1px" }}
            >
              <div
                key={input}
                className="bg-primary"
                style={{
                  width: "100%",
                  height: "100%",
                  animation: "progress 2s ease-in-out forwards",
                  background: "#0d6efd",
                  transformOrigin: "left",
                  transform: "scaleX(0)",
                }}
                onAnimationEnd={() => {
                  setIsLoadingComplete(true);
                  setIsLoading(false);
                }}
              />
              <style>
                {`
                @keyframes progress {
                  0% {
                    transform: scaleX(0);
                  }
                  100% {
                    transform: scaleX(1);
                  }
                }
              `}
              </style>
            </div>
          )}
        </div>
        <div style={{ maxHeight: "250px", overflow: "auto" }} className="px-2">
          {effectiveOptions.map((option, index) => {
            const checked = selectedValues.includes(option.value);
            return (
              <div
                className="form-check align-items-center mt-3 d-flex gap-1"
                key={`${option.value}-${index}`}
              >
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  id={`auto-${filter.name}-${index}`}
                  value={option.value}
                  checked={checked}
                  onChange={() => toggleSelection(option.value, option.label)}
                />
                <label
                  className="form-check-label d-flex align-items-center gap-2 text-capitalize"
                  htmlFor={`auto-${filter.name}-${index}`}
                >
                  {["Name", "Agency", "Team/Property", "Business"].includes(
                    filter.displayName
                  ) && (
                    <Avatar
                      text={option.label}
                      logo={option?.logo}
                      baseURL={option?.baseURL}
                    />
                  )}
                  <span>{option.label}</span>
                </label>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RenderAutoSelect;
